import type {Iteration} from '../api/columns/contracts/iteration'
import type {ItemType} from '../api/memex-items/item-type'
import {
  type LayoutSettings,
  RoadmapDateFieldNone,
  type RoadmapLayoutSettings,
  RoadmapZoomLevel,
} from '../api/view/contracts'
import type {ColumnModel} from '../models/column-model'
import {isDateColumnModel, isIterationColumnModel} from '../models/column-model/guards'
import type {MemexItemModel} from '../models/memex-item-model'
import {formatDateRangeUtc} from '../pages/roadmap/date-utils'
import {RoadmapResources} from '../strings'
import {
  getIssueFieldErrorMessage,
  getIssueFieldValidationResult,
  issueFieldValuesAllowed,
} from './issue-field-value-helpers'
import {intervalDateRange} from './iterations'

/**
 * Time span for a potential or concrete roadmap item.
 * Includes valid start and end date range for the item, limited by discrete values if iterations are used.
 * If the roadmap is configured to use an iteration field(s):
 *    Includes the corresponding start/end iterations if they exist.
 *    Includes undefined start/end dates if the corresponding iteration does not exist.
 */
export type TimeSpan = {
  start: Date | undefined
  end: Date | undefined
  startIteration?: Iteration
  endIteration?: Iteration
}

/**
 * In a deserialized view state, dateFields for roadmap.layoutSettings can be ['none', or ColumnModel]
 */
export type RoadmapColumn = ColumnModel | RoadmapDateFieldNone
export type ReadonlyRoadmapColumns = ReadonlyArray<RoadmapColumn>

export function isRoadmapColumnModel(column: ColumnModel | RoadmapDateFieldNone): column is ColumnModel {
  return column !== RoadmapDateFieldNone && (isDateColumnModel(column) || isIterationColumnModel(column))
}

/**
 * Types used for determining if any date column ids have been selected for roadmap.layoutSettings
 */
type RoadmapLayoutSettingsWithDateColumns = Required<RoadmapLayoutSettings>

export function isRoadmapLayoutSettingsWithDateColumns(
  x: RoadmapLayoutSettings | RoadmapLayoutSettingsWithDateColumns | undefined,
): x is RoadmapLayoutSettingsWithDateColumns {
  return !!(x && x.dateFields && x.dateFields.length > 0)
}

const RoadmapDefaultZoomLevel = RoadmapZoomLevel.Month
export function getZoomLevelFromSettingsOrDefault(layoutSettings: LayoutSettings | undefined): RoadmapZoomLevel {
  return layoutSettings?.roadmap?.zoomLevel ?? RoadmapDefaultZoomLevel
}

export function applyNoneFallback<T>(dateFields: ReadonlyArray<T>): Array<T | RoadmapDateFieldNone> {
  return [dateFields[0] || RoadmapDateFieldNone, dateFields[1] || RoadmapDateFieldNone]
}

/**
 * Returns the tooltip text for the iteration options of a roadmap marker
 * Currently used in iteration marker headers & nubs
 */
export function getIterationOptionsTooltipText(options: Array<Iteration>) {
  return options
    .map(option => {
      const {startDate: start, endDate: end} = intervalDateRange({
        startDate: option.startDate,
        duration: option.duration,
      })

      return `${option.title}: ${formatDateRangeUtc(start, end, options.length > 1 ? 'MMM d' : 'EEE, MMM d')}`
    })
    .join(', ')
}

export function getNewDateFields(currentCols: Array<RoadmapColumn>, newCols: Array<ColumnModel>) {
  const [start = RoadmapDateFieldNone, end = RoadmapDateFieldNone] = currentCols

  // If start and end date are already set, do nothing
  if (start !== RoadmapDateFieldNone && end !== RoadmapDateFieldNone) return undefined

  const newDateFields = newCols.filter(isRoadmapColumnModel)
  // If new fields aren't date fields, do nothing
  if (newDateFields.length === 0) return undefined

  const nextFields: Array<RoadmapColumn> = [start, end]

  const firstNewField = newDateFields[0] as RoadmapColumn
  const isFirstFieldIteration = isIterationColumnModel(firstNewField as ColumnModel)

  if (start === RoadmapDateFieldNone && end !== RoadmapDateFieldNone) {
    nextFields[0] = firstNewField
  } else if (start !== RoadmapDateFieldNone && end === RoadmapDateFieldNone) {
    nextFields[1] = firstNewField
  } else if (start === RoadmapDateFieldNone && end === RoadmapDateFieldNone) {
    // If both start and end are unset, set both to the new field if it's an iteration
    if (isFirstFieldIteration) {
      nextFields[0] = firstNewField
      nextFields[1] = firstNewField
    } else {
      nextFields[0] = firstNewField

      // If multiple date fields are added, include an end date
      if (newDateFields.length > 1) {
        nextFields[1] = newDateFields[1] as RoadmapColumn
      }
    }
  }

  return nextFields
}

function isRoadmapIssueFieldColumn(column: RoadmapColumn) {
  return column !== RoadmapDateFieldNone && column.isIssueField
}

export function isDateFieldEditableForItem(item: MemexItemModel, column: RoadmapColumn) {
  if (!isRoadmapIssueFieldColumn(column)) return true
  return issueFieldValuesAllowed(item.contentType, item.getOwner() || '')
}

function getUpdateErrorMessage(dateFields: ReadonlyRoadmapColumns, startDateError?: string, endDateError?: string) {
  const numAssignableFields = dateFields.filter(df => df !== RoadmapDateFieldNone).length
  // There is only an update error message if both fields cannot be assigned
  const hasUpdateError =
    numAssignableFields === 2
      ? Boolean(startDateError) && Boolean(endDateError)
      : Boolean(startDateError) || Boolean(endDateError)

  if (!hasUpdateError) return undefined

  return `${RoadmapResources.unableToSetDateFields}: ${startDateError || endDateError}`
}

function getExpandErrorMessage(startDateError?: string, endDateError?: string) {
  // If only one date field cannot be assigned, the selection cannot be expanded
  const hasExpandError = Boolean(startDateError) || Boolean(endDateError)

  if (!hasExpandError) return undefined

  return `${RoadmapResources.unableToExpandDateRange}: ${startDateError || endDateError}`
}

function getErrorMessageForRoadmapColumn(column: RoadmapColumn, itemType: ItemType, contentOwner: string) {
  if (!isRoadmapIssueFieldColumn(column)) return undefined

  const columnValidation = getIssueFieldValidationResult(itemType, contentOwner)

  if (columnValidation.editable) return undefined

  return getIssueFieldErrorMessage(columnValidation.reason)
}

export function getIssueFieldInvalidOperations(item: MemexItemModel, dateFields: ReadonlyRoadmapColumns) {
  const datesWithFallback = applyNoneFallback(dateFields)
  const startDateColumn = datesWithFallback[0]
  const endDateColumn = datesWithFallback[1]

  const itemOwner = item.getOwner() || ''
  const itemType = item.contentType

  const startDateErrorMessage = getErrorMessageForRoadmapColumn(startDateColumn, itemType, itemOwner)
  const endDateErrorMessage = getErrorMessageForRoadmapColumn(endDateColumn, itemType, itemOwner)

  return {
    updateError: getUpdateErrorMessage(dateFields, startDateErrorMessage, endDateErrorMessage),
    expandError: getExpandErrorMessage(startDateErrorMessage, endDateErrorMessage),
  }
}
